import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/components/TailwindComponents/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-m-primary text-white shadow-md hover:bg-m-primary-dark hover:shadow-lg active:bg-m-primary-dark active:shadow-md',
        destructive:
          'bg-red-500 text-white shadow-md hover:bg-red-600 hover:shadow-lg active:bg-red-700 active:shadow-md',
        success:
          'bg-m-success text-m-success-foreground shadow-md hover:bg-m-success-dark hover:text-white hover:shadow-lg active:bg-m-success-dark active:text-white active:shadow-md',
        outline:
          'border border-gray-300 dark:border-gray-600 bg-transparent text-gray-800 dark:text-gray-200 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-400 hover:text-gray-900 dark:hover:text-white active:bg-gray-200 dark:active:bg-gray-600',
        secondary:
          'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow active:bg-gray-400 dark:active:bg-gray-500 active:shadow-sm',
        ghost:
          'bg-transparent text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white active:bg-gray-200 dark:active:bg-gray-600',
        link: 'bg-transparent text-blue-600 dark:text-blue-400 underline-offset-4 hover:text-blue-800 dark:hover:text-blue-300 hover:underline',
      },
      size: {
        default: 'h-10 rounded px-4 py-2',
        sm: 'h-8 rounded px-3 text-xs',
        lg: 'h-12 rounded px-8 text-base',
        icon: 'h-10 w-10 rounded',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
